'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '@/app/lib/posthog';
import ConstellationBackdrop from './ConstellationBackdrop';
import DayBackdrop from './DayBackdrop';
import type { HeroVariant, HeroVideoKind } from './heroVariants';
import LandingIcon from './LandingIcon';
import VideoBackdrop from './VideoBackdrop';
import styles from './LandingHome.module.css';
import { HIDDEN_ROUTES, loadSnapshot, prefersReducedMotion, type BusSnapshot, type CampusSnapshot } from './landingData';

const HERO_FEATURES: Array<[label: string, href: string, icon: string]> = [
  ['Schedule builder', '/scheduling', 'calendar'],
  ['Degree planner', '/degree-planning', 'cap'],
  ['Course Sniper', '/course-sniper', 'target'],
  ['SIRS ratings', '/sirs', 'star'],
  ['Bus tracker', '/bus-tracker', 'bus'],
  ['Parking', '/parking-map', 'parking'],
  ['Dining', '/dining', 'food'],
  ['Study rooms', '/room-availability', 'door'],
  ['Events', '/events', 'ticket'],
  ['Ask Scarlet Sync', '/ai-assistant', 'sparkle'],
];

// The texture is a square crop of the campus map that covers every New Brunswick campus.
const TEX = 2048;
const CROP = { x: -110, y: 30, s: 1240 };
const toTex = (x: number, y: number): [number, number] => [(x - CROP.x) / CROP.s, (y - CROP.y) / CROP.s];

// Slow camera loop: Livingston → Busch → College Ave → Cook/Douglass → College Ave → Busch → …
const TOUR = [[700, 175], [270, 200], [480, 680], [740, 1120], [480, 680], [270, 200]].map(([x, y]) => toTex(x, y));
const MOVE_SECONDS = 18;
const HOLD_SECONDS = 7;
const MAX_BUSES = 48;
const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);

function tourAt(seconds: number): [number, number] {
  const leg = MOVE_SECONDS + HOLD_SECONDS;
  const k = Math.floor(seconds / leg) % TOUR.length;
  const t = seconds % leg;
  const a = TOUR[k];
  const b = TOUR[(k + 1) % TOUR.length];
  const f = t < HOLD_SECONDS ? 0 : ease((t - HOLD_SECONDS) / MOVE_SECONDS);
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

const VERTEX = `#version 300 es
in vec2 p; void main(){ gl_Position = vec4(p, 0., 1.); }`;

// Map luminance + drifting noise + bus glows, quantized with an 8×8 Bayer matrix into three scarlet tones.
const FRAGMENT = `#version 300 es
precision highp float; out vec4 o;
uniform sampler2D map; uniform vec2 res; uniform float t; uniform vec2 mouse; uniform float px; uniform vec2 center; uniform float span;
uniform vec2 buses[${MAX_BUSES}]; uniform int nb;
float bayer(vec2 c){ ivec2 p = ivec2(mod(c, 8.)); int x = p.x, y = p.y, xy = x ^ y;
  int v = ((xy & 1) << 5) | ((x & 1) << 4) | ((xy & 2) << 2) | ((x & 2) << 1) | ((xy & 4) >> 1) | ((x & 4) >> 2);
  return float(v) / 64.; }
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.-2.*f);
  return mix(mix(hash(i), hash(i+vec2(1,0)), f.x), mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), f.x), f.y); }
void main(){
  vec2 cell = floor(gl_FragCoord.xy / px);
  vec2 q = (cell * px + px * .5) / res;
  float aspect = res.x / res.y;
  vec2 m = vec2((q.x - .5) * aspect, .5 - q.y);
  vec2 st = center + m * span + (mouse - .5) * vec2(.012, -.012);
  float inside = step(0., st.x) * step(st.x, 1.) * step(0., st.y) * step(st.y, 1.);
  float lum = texture(map, clamp(st, 0., 1.)).r * inside;
  float n = noise(st * 9. + vec2(t * .05, -t * .035)) * .6 + noise(st * 22. - t * .06) * .4;
  float v = lum * .75 + n * .2;
  for (int i = 0; i < ${MAX_BUSES}; i++) { if (i >= nb) break; float d = distance(st, buses[i]) / span; v += .55 * exp(-d * d * 2600.); }
  float md = distance(q * vec2(aspect, 1.), mouse * vec2(aspect, 1.)); v += .28 * exp(-md * md * 18.);
  v *= 1. - .5 * smoothstep(.35, 1.1, length(q - .5) * 1.4);
  float d = bayer(cell);
  vec3 back = vec3(.035, .006, .01), mid = vec3(.80, 0., .2), hi = vec3(1., .86, .84);
  o = vec4(v > d ? (v > .78 + d * .3 ? hi : mid) : back, 1.);
}`;

type Vehicle = { pts: Array<[number, number]>; pos: number; speed: number };

function paintCampus(campus: CampusSnapshot, routes: BusSnapshot['routes']) {
  const src = document.createElement('canvas');
  src.width = TEX;
  src.height = TEX;
  const g = src.getContext('2d');
  if (!g) return src;
  const s = TEX / CROP.s;
  g.fillStyle = '#000';
  g.fillRect(0, 0, TEX, TEX);
  g.setTransform(s, 0, 0, s, -CROP.x * s, -CROP.y * s);
  g.fillStyle = '#3a3a3a';
  g.fill(new Path2D(campus.parking.join('')));
  g.fillStyle = '#9a9a9a';
  g.fill(new Path2D(campus.buildings.join('')));
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.strokeStyle = '#fff';
  g.lineWidth = 2.4;
  routes.forEach((r) => r.d.forEach((d) => g.stroke(new Path2D(d))));
  return src;
}

/** Samples each route's longest drawn segment in texture space so buses can glide along it. */
function sampleRoutes(routes: BusSnapshot['routes']) {
  const tmp = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const sampled: Record<string, Array<[number, number]>> = {};
  routes.forEach((r) => {
    let best: { length: number; pts: Array<[number, number]> } | null = null;
    r.d.forEach((d) => {
      tmp.setAttribute('d', d);
      const length = tmp.getTotalLength();
      if (best && length <= best.length) return;
      const pts: Array<[number, number]> = [];
      for (let t = 0; t <= length; t += 4) {
        const q = tmp.getPointAtLength(t);
        pts.push(toTex(q.x, q.y));
      }
      best = { length, pts };
    });
    if (best) sampled[r.id] = (best as { pts: Array<[number, number]> }).pts;
  });
  return sampled;
}

function placeVehicles(list: Array<{ route: string; x: number; y: number }>, sampled: Record<string, Array<[number, number]>>): Vehicle[] {
  return list.filter((b) => sampled[b.route]?.length).slice(0, MAX_BUSES).map((b) => {
    const pts = sampled[b.route];
    const [bx, by] = toTex(b.x, b.y);
    let pos = 0;
    let bestDistance = Infinity;
    pts.forEach(([x, y], i) => {
      const d = (x - bx) ** 2 + (y - by) ** 2;
      if (d < bestDistance) { bestDistance = d; pos = i; }
    });
    return { pts, pos, speed: 0.2 + Math.random() * 0.15 };
  });
}

function compile(gl: WebGL2RenderingContext) {
  const shader = (type: number, code: string) => {
    const s = gl.createShader(type);
    if (!s) throw new Error('shader');
    gl.shaderSource(s, code);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader');
    return s;
  };
  const prog = gl.createProgram();
  if (!prog) throw new Error('program');
  gl.attachShader(prog, shader(gl.VERTEX_SHADER, VERTEX));
  gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, FRAGMENT));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) ?? 'link');
  return prog;
}

export default function DitherHero({ variant, videoKind, onVideoFail }: { variant: HeroVariant | null; videoKind: HeroVideoKind; onVideoFail: () => void }) {
  const heroRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fallbackRef = useRef<HTMLCanvasElement>(null);
  const [noGl, setNoGl] = useState(false);

  useEffect(() => {
    if (variant !== 'buses') return;
    let disposed = false;
    const cleanups: Array<() => void> = [];

    Promise.all([loadSnapshot('campus'), loadSnapshot('bus')]).then(([campus, bus]) => {
      const hero = heroRef.current;
      const canvas = canvasRef.current;
      const fallback = fallbackRef.current;
      if (disposed || !hero || !canvas || !fallback) return;
      const routes = bus.routes.filter((r) => !HIDDEN_ROUTES.has(r.short));
      const src = paintCampus(campus, routes);
      fallback.width = 1024;
      fallback.height = 1024;
      fallback.getContext('2d')?.drawImage(src, 0, 0, 1024, 1024);

      const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, powerPreference: 'low-power' });
      let prog: WebGLProgram;
      try {
        if (!gl) throw new Error('no webgl2');
        prog = compile(gl);
      } catch {
        setNoGl(true);
        return;
      }
      gl.useProgram(prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'p');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      const U = (n: string) => gl.getUniformLocation(prog, n);
      const uRes = U('res'), uT = U('t'), uMouse = U('mouse'), uPx = U('px'), uBus = U('buses'), uNb = U('nb'), uCenter = U('center'), uSpan = U('span');

      const sampled = sampleRoutes(routes);
      let vehicles = placeVehicles(bus.buses, sampled);
      const busArr = new Float32Array(MAX_BUSES * 2);
      const reduce = prefersReducedMotion();
      let mouse: [number, number] = [0.5, 0.5];
      const onPointer = (e: PointerEvent) => { mouse = [e.clientX / window.innerWidth, 1 - e.clientY / window.innerHeight]; };
      window.addEventListener('pointermove', onPointer, { passive: true });
      cleanups.push(() => window.removeEventListener('pointermove', onPointer));

      // Reduced resolution: the dither hides it and it keeps GPU cost low.
      const resize = () => {
        const scale = Math.min(1, window.devicePixelRatio) * 0.75;
        canvas.width = Math.max(1, Math.round(hero.clientWidth * scale));
        canvas.height = Math.max(1, Math.round(hero.clientHeight * scale));
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform2f(uRes, canvas.width, canvas.height);
        gl.uniform1f(uPx, Math.max(2, Math.round(canvas.width / 440)));
        gl.uniform1f(uSpan, Math.min(0.5, (560 / CROP.s) * (canvas.width < canvas.height ? 1.35 : 1)));
      };
      resize();
      window.addEventListener('resize', resize);
      cleanups.push(() => window.removeEventListener('resize', resize));

      const t0 = performance.now();
      const draw = (now: number, dt: number) => {
        gl.uniform1i(uNb, vehicles.length);
        vehicles.forEach((v, i) => {
          v.pos = (v.pos + (v.speed * dt) / 16) % v.pts.length;
          const [x, y] = v.pts[Math.floor(v.pos)];
          busArr[i * 2] = x;
          busArr[i * 2 + 1] = y;
        });
        const seconds = (now - t0) / 1000;
        const [cx, cy] = tourAt(reduce ? 0 : seconds + HOLD_SECONDS * 0.6);
        gl.uniform2f(uCenter, cx, cy);
        gl.uniform2fv(uBus, busArr);
        gl.uniform1f(uT, seconds);
        gl.uniform2f(uMouse, mouse[0], mouse[1]);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      };
      draw(performance.now(), 0);

      let visible = true;
      let raf = 0;
      let last = performance.now();
      const frame = (now: number) => {
        raf = 0;
        if (!visible || document.hidden || disposed) return;
        // rAF timestamps can trail performance.now(); never step backwards.
        const dt = Math.max(0, Math.min(64, now - last));
        last = now;
        draw(now, dt);
        raf = requestAnimationFrame(frame);
      };
      const start = () => {
        if (reduce || raf || !visible || document.hidden) return;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      };
      const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; start(); });
      io.observe(hero);
      const onVisibility = () => start();
      document.addEventListener('visibilitychange', onVisibility);
      cleanups.push(() => {
        io.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        if (raf) cancelAnimationFrame(raf);
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      });

      // Swap the snapshot for live vehicle positions once the page is idle.
      const refresh = () => {
        fetch('/api/bus/map-buses', { signal: AbortSignal.timeout(6000) })
          .then((res) => (res.ok ? res.json() : null))
          .then((live: { buses?: Array<{ routeId: number | string; latitude: number | null; longitude: number | null }> } | null) => {
            if (disposed || !live?.buses) return;
            const P = bus.projection;
            const B = P.bounds;
            const list = live.buses
              .filter((b) => b.latitude != null && b.longitude != null && b.latitude >= B.south && b.latitude <= B.north && b.longitude >= B.west && b.longitude <= B.east)
              .map((b) => ({ route: String(b.routeId), x: ((b.longitude as number) - P.west) * P.cosLat * P.scale, y: (P.north - (b.latitude as number)) * P.scale }));
            if (list.length) vehicles = placeVehicles(list, sampled);
          })
          .catch(() => { /* keep the snapshot */ });
      };
      const idle = window.requestIdleCallback ? window.requestIdleCallback(refresh, { timeout: 4000 }) : window.setTimeout(refresh, 2500);
      cleanups.push(() => (window.cancelIdleCallback ? window.cancelIdleCallback(idle) : window.clearTimeout(idle)));
    }).catch(() => setNoGl(true));

    return () => {
      disposed = true;
      cleanups.forEach((fn) => fn());
    };
  }, [variant]);

  return (
    <header ref={heroRef} className={`${styles.hero} ${noGl ? styles.noGl : ''}`}>
      {variant === 'buses' ? (
        <>
          <canvas ref={fallbackRef} className={styles.fallback} aria-hidden="true" />
          <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
        </>
      ) : null}
      {variant === 'day' ? <DayBackdrop /> : null}
      {variant === 'constellation' ? <ConstellationBackdrop /> : null}
      {variant === 'video' ? <VideoBackdrop kind={videoKind} onFail={onVideoFail} /> : null}
      <div className={styles.heroCopy}>
        <h1 className={styles.heroTitle}>All of Rutgers.<span>One app.</span></h1>
        <div className={styles.heroRow}>
          <ul className={styles.heroIcons} aria-label="What Scarlet Sync does">
            {HERO_FEATURES.map(([label, href, icon]) => (
              <li key={href}>
                <Link href={href} aria-label={label} onClick={() => trackEvent('landing_cta_click', { cta: icon, location: 'hero_icons' })}>
                  <LandingIcon name={icon} className={styles.ico} />
                </Link>
              </li>
            ))}
          </ul>
          <div className={styles.ctas}>
            <Link className={styles.btnPrimary} href="/scheduling" onClick={() => trackEvent('landing_cta_click', { cta: 'start_planning_free', location: 'hero' })}>Start planning for free</Link>
            <a className={styles.btnGhost} href="#everything">See everything</a>
          </div>
        </div>
      </div>
    </header>
  );
}
